import { useEffect, useState } from "react";
import {
  analyticsUrl,
  ensureAnalyticsSession
} from "app/data/services/AnalyticsSession";
import {
  loadTelemetryPreferences,
  saveTelemetryPreferences,
  telemetryPreferenceKey,
  TelemetryRange,
  Visualization
} from "app/data/utils/telemetryPreferences";

type Props = {
  userId: string;
  selectedTenantId: string;
  accountId: string;
  objectId: string;
};
type Field = {
  uniqueId: string;
  name: string;
  preferredVisualization: "chart" | "kpi";
};

const rangeMilliseconds: Record<TelemetryRange, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000
};
const chartAggregationMinutes: Record<TelemetryRange, string> = {
  "1h": "",
  "24h": "5",
  "7d": "15"
};
const timeWindow = (range: TelemetryRange) => {
  const to = Date.now();
  return { from: to - rangeMilliseconds[range], to };
};

export default function GrafanaDashboard(props: Props) {
  // Remount on identity/context changes before exposing previous fields or preferences.
  return (
    <TelemetryView
      key={JSON.stringify([
        props.userId,
        props.selectedTenantId,
        props.accountId,
        props.objectId
      ])}
      {...props}
    />
  );
}

function TelemetryView({
  userId,
  selectedTenantId,
  accountId,
  objectId
}: Props) {
  const preferenceKey = telemetryPreferenceKey(
    userId,
    selectedTenantId,
    accountId,
    objectId
  );
  const [preferences, setPreferences] = useState(() =>
    loadTelemetryPreferences(preferenceKey)
  );
  const [queryWindow, setQueryWindow] = useState(() =>
    timeWindow(preferences.range)
  );
  const context = `${queryWindow.from}:${queryWindow.to}`;
  const [loadedContext, setLoadedContext] = useState("");
  const [fields, setFields] = useState<Field[]>([]);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [revision, setRevision] = useState(0);

  useEffect(
    () => saveTelemetryPreferences(preferenceKey, preferences),
    [preferenceKey, preferences]
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setError(false);
    setLoadedContext("");
    const initialize = async () => {
      try {
        await ensureAnalyticsSession();
        const parameters = new URLSearchParams({
          selectedTenantId,
          from: new Date(queryWindow.from).toISOString(),
          to: new Date(queryWindow.to).toISOString()
        });
        const loadFields = () =>
          fetch(
            analyticsUrl(
              `/analytics/query/accounts/${accountId}/objects/${objectId}/timeseries/fields?${parameters}`
            ),
            { credentials: "include", signal: controller.signal }
          );
        let response = await loadFields();
        if (response.status === 401) {
          await ensureAnalyticsSession();
          response = await loadFields();
        }
        if (!response.ok) throw new Error("Fields unavailable.");
        const data: Field[] = await response.json();
        if (
          !Array.isArray(data) ||
          data.some(
            (field) =>
              typeof field?.uniqueId !== "string" ||
              typeof field?.name !== "string" ||
              (field?.preferredVisualization !== "chart" &&
                field?.preferredVisualization !== "kpi")
          )
        )
          throw new Error("Invalid fields.");
        if (!cancelled) {
          setFields(data);
          setPreferences((current) => ({
            ...current,
            fieldId: data.some((field) => field.uniqueId === current.fieldId)
              ? current.fieldId
              : (data[0]?.uniqueId ?? "")
          }));
          setLoadedContext(context);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    initialize();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    context,
    selectedTenantId,
    accountId,
    objectId,
    retry,
    queryWindow.from,
    queryWindow.to
  ]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setInterval(() => {
      ensureAnalyticsSession()
        .then(({ created }) => {
          if (cancelled) return;
          if (created) setRevision((value) => value + 1);
          setQueryWindow(timeWindow(preferences.range));
        })
        .catch(() => {
          if (!cancelled) {
            setLoadedContext("");
            setError(true);
          }
        });
    }, 60000);
    const onLogout = () => {
      setLoadedContext("");
      setError(true);
    };
    const onSessionCreated = () => setRevision((value) => value + 1);
    window.addEventListener(
      "allsetra-analytics-session-created",
      onSessionCreated
    );
    window.addEventListener("allsetra-analytics-logout", onLogout);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(
        "allsetra-analytics-session-created",
        onSessionCreated
      );
      window.removeEventListener("allsetra-analytics-logout", onLogout);
    };
  }, [preferences.range]);

  const selectedField = fields.find(
    (field) => field.uniqueId === preferences.fieldId
  );
  const mode =
    preferences.mode === "auto"
      ? (selectedField?.preferredVisualization ?? "chart")
      : preferences.mode;
  const dashboard =
    mode === "kpi"
      ? "customer-object-telemetry-kpi"
      : "customer-object-telemetry";
  const parameters = new URLSearchParams({
    "var-selectedTenantId": selectedTenantId,
    "var-accountId": accountId,
    "var-objectId": objectId,
    "var-fieldId": preferences.fieldId,
    from: String(queryWindow.from),
    to: String(queryWindow.to),
    kiosk: "",
    theme: "light"
  });
  if (mode === "chart") {
    parameters.set(
      "var-aggregateMinutes",
      selectedField?.preferredVisualization === "chart"
        ? chartAggregationMinutes[preferences.range]
        : ""
    );
  }
  const loaded = loadedContext === context;
  return (
    <section style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "16px",
          marginBottom: "12px"
        }}
      >
        <label>
          Time range{" "}
          <select
            value={preferences.range}
            onChange={(event) => {
              const range = event.target.value as TelemetryRange;
              setPreferences((current) => ({ ...current, range }));
              setQueryWindow(timeWindow(range));
            }}
          >
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
          </select>
        </label>
        <label>
          Visualization{" "}
          <select
            value={preferences.mode}
            onChange={(event) => {
              const mode = event.target.value as Visualization;
              setPreferences((current) => ({ ...current, mode }));
            }}
          >
            <option value="auto">Auto</option>
            <option value="chart">Chart</option>
            <option value="kpi">KPI</option>
          </select>
        </label>
        {loaded && !error && fields.length > 0 && (
          <label>
            Telemetry field{" "}
            <select
              value={preferences.fieldId}
              onChange={(event) => {
                const fieldId = event.target.value;
                setPreferences((current) => ({ ...current, fieldId }));
              }}
            >
              {fields.map((field) => (
                <option key={field.uniqueId} value={field.uniqueId}>
                  {field.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {error ? (
        <div role="alert">
          <p>Telemetry dashboard is unavailable.</p>
          <button onClick={() => setRetry((value) => value + 1)}>Retry</button>
        </div>
      ) : !loaded ? (
        <p role="status">Loading telemetry dashboard...</p>
      ) : !fields.length ? (
        <p>No telemetry fields have data in this time range.</p>
      ) : (
        <iframe
          key={`${context}:${preferences.fieldId}:${mode}:${revision}`}
          title="Object telemetry dashboard"
          src={analyticsUrl(
            `/analytics/grafana/d/${dashboard}/object-telemetry?${parameters}`
          )}
          style={{ width: "100%", height: "650px", border: 0 }}
        />
      )}
    </section>
  );
}
